import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "who.user.token";

const UserAuthCtx = createContext(null);

export function UserAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const refresh = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setUser(r.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data;
  };

  const signUp = async (payload) => {
    const r = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data;
  };

  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const verifyEmail = (verifyToken) =>
    axios.post(`${API}/auth/verify-email`, { token: verifyToken }).then((r) => r.data);

  return (
    <UserAuthCtx.Provider
      value={{
        token,
        user,
        loading,
        signIn,
        signUp,
        signOut,
        verifyEmail,
        refresh,
        authHeaders,
        API,
      }}
    >
      {children}
    </UserAuthCtx.Provider>
  );
}

export function useUserAuth() {
  const ctx = useContext(UserAuthCtx);
  if (!ctx) throw new Error("useUserAuth must be used inside UserAuthProvider");
  return ctx;
}
